import { usePostHog } from 'posthog-js/react';
import { installUnhandledExceptionCapture } from './exceptionCapture';

/**
 * Error tracking utilities for manual exception capture
 * Following PostHog best practices
 */

/**
 * Custom hook for error tracking in React components
 */
export const useErrorTracking = () => {
  const posthog = usePostHog();

  /**
   * Manually capture an exception with optional additional properties
   * @param error - The error to capture
   * @param additionalProperties - Additional context/properties to include
   */
  const captureException = (error: Error | string, additionalProperties?: Record<string, any>) => {
    if (!posthog) {
      console.warn('PostHog not initialized. Error tracking skipped.');
      return;
    }

    try {
      // Convert string errors to Error objects for better stack traces
      const errorObject = typeof error === 'string' ? new Error(error) : error;
      
      // Capture the exception with PostHog
      posthog.captureException(errorObject, {
        // Add timestamp
        timestamp: new Date().toISOString(),
        // Add current URL for context
        current_url: window.location.href,
        // Add user agent for debugging
        user_agent: navigator.userAgent,
        // Merge in additional properties
        ...additionalProperties
      });

      // Also log to console for development
      if (process.env.NODE_ENV === 'development') {
        console.error('Manual exception captured:', errorObject, additionalProperties);
      }
    } catch (trackingError) {
      // Don't let error tracking break the app
      console.warn('Failed to capture exception:', trackingError);
    }
  };

  /**
   * Capture a handled error (for try/catch blocks)
   * @param error - The error that was caught
   * @param context - Context about where the error occurred
   */
  const captureHandledError = (error: Error | string, context?: {
    function?: string;
    component?: string;
    action?: string;
    [key: string]: any;
  }) => {
    captureException(error, {
      error_type: 'handled_error',
      ...context
    });
  };

  /**
   * Capture a user-reported issue/bug
   * @param description - User's description of the issue
   * @param context - Additional context about the issue
   */
  const captureUserReportedIssue = (description: string, context?: {
    severity?: 'low' | 'medium' | 'high';
    category?: string;
    steps_to_reproduce?: string;
    [key: string]: any;
  }) => {
    captureException(new Error(`User reported: ${description}`), {
      error_type: 'user_reported',
      user_description: description,
      severity: context?.severity || 'medium',
      ...context
    });
  };

  /**
   * Capture API/Network errors with request context
   * @param error - The network error
   * @param requestContext - Details about the failed request
   */
  const captureNetworkError = (error: Error | string, requestContext?: {
    url?: string;
    method?: string;
    status_code?: number;
    response_body?: any;
    [key: string]: any;
  }) => {
    captureException(error, {
      error_type: 'network_error',
      ...requestContext
    });
  };

  return {
    captureException,
    captureHandledError,
    captureUserReportedIssue,
    captureNetworkError
  };
};

/**
 * Non-hook version for use outside React components
 */
class ErrorTracker {
  private static instance: ErrorTracker;
  private posthog: any = null;

  private constructor() {
    // Initialize PostHog reference when available
    if (typeof window !== 'undefined' && (window as any).posthog) {
      this.posthog = (window as any).posthog;
    }
  }

  public static getInstance(): ErrorTracker {
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  public setPostHog(posthogInstance: any) {
    this.posthog = posthogInstance;
  }

  public getPostHog(): any {
    return this.posthog;
  }

  public captureException(error: Error | string, additionalProperties?: Record<string, any>) {
    if (!this.posthog) {
      console.warn('PostHog not initialized. Error tracking skipped.');
      return;
    }

    try {
      const errorObject = typeof error === 'string' ? new Error(error) : error;
      
      this.posthog.captureException(errorObject, {
        timestamp: new Date().toISOString(),
        current_url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        ...additionalProperties
      });

      if (process.env.NODE_ENV === 'development') {
        console.error('Manual exception captured:', errorObject, additionalProperties);
      }
    } catch (trackingError) {
      console.warn('Failed to capture exception:', trackingError);
    }
  }
}

// Export singleton instance for non-React usage
export const errorTracker = ErrorTracker.getInstance();

/**
 * Global error handlers (optional - for cases not covered by PostHog autocapture)
 */
export const setupGlobalErrorHandlers = () => {
  const client = errorTracker.getPostHog();
  if (client && typeof client.captureException === 'function') {
    installUnhandledExceptionCapture(client);
  }
};

// Example usage patterns:
/*
// In React components:
const { captureException, captureHandledError } = useErrorTracking();

// Handle caught errors
try {
  riskyOperation();
} catch (error) {
  captureHandledError(error, {
    function: 'riskyOperation',
    component: 'MyComponent'
  });
}

// Outside React components:
import { errorTracker } from './utils/errorTracking';

try {
  apiCall();
} catch (error) {
  errorTracker.captureException(error, { context: 'api_call' });
}
*/
